import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';

interface GamemodeImage {
  _id: string;
  gamemode: string;
  image_url: string;
  active: boolean;
  created_at: string;
}

interface GamemodeImageListProps {
  onAddClick: () => void;
}

export function GamemodeImageList({ onAddClick }: GamemodeImageListProps) {
  const [images, setImages] = useState<GamemodeImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(API_ENDPOINTS.admin.gamemodeImages, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await response.json();
      setImages(data.images);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleToggle = async (id: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(API_ENDPOINTS.admin.toggleGamemodeImage(id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to toggle image status');
      }

      // Refresh the list
      fetchImages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle image status');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(API_ENDPOINTS.admin.gamemodeImage(id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      // Refresh the list
      fetchImages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete image');
    }
  };

  if (loading) {
    return <div className="text-center text-gray-400">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white">Gamemode Images</h2>
        <button
          onClick={onAddClick}
          className="px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Add New Image
        </button>
      </div>

      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {images.map((image) => (
          <div
            key={image._id}
            className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700"
          >
            <div className="aspect-w-16 aspect-h-9">
              <img
                src={image.image_url}
                alt={`${image.gamemode} screenshot`}
                className="object-cover"
              />
            </div>
            <div className="p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-300 capitalize">
                  {image.gamemode}
                </span>
                <span className={`text-sm ${image.active ? 'text-green-500' : 'text-red-500'}`}>
                  {image.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => handleToggle(image._id)}
                  className="px-3 py-1 text-sm font-medium rounded-md text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  {image.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(image._id)}
                  className="px-3 py-1 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 